import { action, makeObservable, observable, reaction } from 'mobx';

import { getRequest, postRequest } from '../../services/api';
import type { ApiResponse } from '../../services/types';
import { getErrorMessage } from '../../utils/errors';
import { devError } from '../../utils/log';
import type { ExtendedCoinType } from '../cointype/CoinTypeStore';
import type { LaunchGameData } from '../game/types';
import type { RootStore } from '../RootStore';
import { EnumOpenDialog } from '../ui/types';

// Launch session coin = GC, matching the "buy GC, get a free pack" purchase framing (the platform's
// launchable coin is GC/SC only — 'CASH' isn't valid in CoinTypeEnum). The session coin is launch
// context ONLY — the core adapter settles open/sell/ship in CASH regardless — so this does not lock in
// the economic model, and GC avoids the SC-mode + phone-verification launch gates.
const CROWN_RIPS_COIN: ExtendedCoinType = 'GC';
const CROWN_RIPS_PATH = 'crown-rips';

// Safely pull a query param out of an absolute URL (returns undefined on a malformed/empty URL).
function getQueryParam(url: string, key: string): string | undefined {
  try {
    return new URL(url).searchParams.get(key) ?? undefined;
  } catch {
    return undefined;
  }
}

interface CrownRipsGameInfo {
  gameId: number;
  identifier: string;
  name: string;
}

export class CrownRipsStore {
  private readonly rootStore: RootStore;

  // De-dupes concurrent resolves so the prefetch + a quick click share one in-flight request.
  private gameIdPromise: Promise<number | null> | null = null;

  // Parallel launch: the iframe is opened before launch-game finishes, then the session is delivered
  // to it via postMessage. These track the two halves of that handshake (either can arrive first).
  private launchSessionId: string | null = null;
  private iframeWindow: Window | null = null;
  private launchInFlight = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);

    // SPA login (landing page → auth): the flag value doesn't change across login, so the
    // useCrownRipsFeature effect won't re-fire — retry the auth-gated game-id fetch here instead.
    reaction(
      () => this.rootStore.authStore.authorized,
      (authorized) => {
        // resolveGameId already no-ops when gameId is set (and dedupes in-flight fetches).
        if (authorized && this.isEnabled) this.prefetchGame();
      },
    );

    // Live-mirror the Sound toggle into the iframe while the game is open (postSoundEnabled no-ops when
    // it isn't). The initial value is delivered with the session handshake in deliverSession().
    reaction(
      () => this.rootStore.uiStore.disabledSounds,
      () => this.postSoundEnabled(),
    );
  }

  // Kill switch (LD `crown_rips_enabled`) — gates the menu entry + the prefetch.
  @observable
  public isEnabled = false;

  // The resolved (auto-assigned) numeric game id, warmed up at app start so the click launches instantly.
  @observable
  public gameId: number | null = null;

  @observable
  public isLoadingGame = false;

  @action
  public setEnabled(enabled: boolean) {
    this.isEnabled = enabled;
    // The widget follows gameId; if the kill switch turns off mid-session, drop it so the widget clears.
    if (!enabled) this.gameId = null;
  }

  @action
  public setIsLoadingGame(loading: boolean) {
    this.isLoadingGame = loading;
  }

  @action
  private setGameId(gameId: number) {
    this.gameId = gameId;
  }

  // Resolve the numeric game id from the stable identifier (server-side), cached + de-duped — nothing is
  // hardcoded/reserved client-side.
  private resolveGameId(): Promise<number | null> {
    if (this.gameId != null) return Promise.resolve(this.gameId);
    if (this.gameIdPromise) return this.gameIdPromise;

    this.gameIdPromise = (async () => {
      try {
        const response = await getRequest<ApiResponse<CrownRipsGameInfo>>('crown-rips/game');
        const gameId = response.data?.data?.gameId ?? null;
        if (gameId != null) this.setGameId(gameId);
        return gameId;
      } catch (error) {
        devError('CrownRipsStore: resolveGameId error', getErrorMessage(error));
        return null;
      } finally {
        this.gameIdPromise = null;
      }
    })();

    return this.gameIdPromise;
  }

  // Fire-and-forget warm-up at app start (when the kill switch is on) so the click has no resolve latency.
  // Anonymous visitors are skipped — /crown-rips/game requires auth (401) — the auth reaction above
  // re-runs this on login.
  public prefetchGame() {
    if (!this.rootStore.authStore.authorized) return;
    void this.resolveGameId();
  }

  public async startCrownRipsGame(options?: { tab?: string }) {
    // Defense-in-depth: the kill switch already hides every entry point, but never launch if it's off
    // (stale UI, race, or a direct call) — the server enforces the same flag on every endpoint.
    if (!this.isEnabled) return;
    this.setIsLoadingGame(true);
    this.launchSessionId = null;
    this.iframeWindow = null;

    try {
      // Uses the prefetched id when available (no round-trip); otherwise resolves on demand.
      const gameId = await this.resolveGameId();

      if (!gameId) {
        devError('CrownRipsStore: startCrownRipsGame error: could not resolve Crown Rips game id');
        this.setIsLoadingGame(false);
        return;
      }

      // Open the iframe NOW — before the slow launch-game call — with `awaitSession` instead of a
      // sessionId. The game-ui bundle, Pixi and the Firebase auth handshake all warm up during the
      // ~600ms launch-game takes; the iframe holds on a loader until we deliver the session below.
      const url = this.rootStore.gameUiStore.getGameUiURL(CROWN_RIPS_PATH, {
        awaitSession: true,
        coinType: CROWN_RIPS_COIN,
        tab: options?.tab,
      });
      this.rootStore.uiStore.setOpenDialog({
        dialog: EnumOpenDialog.isOpenGameUiDialog,
        options: { url, dataTestId: 'crown-rips-game-iframe' },
      });
      // The open dialog (game-ui loader) is now the loading surface — drop the menu-button spinner.
      this.setIsLoadingGame(false);

      void this.launchSession(gameId);
    } catch (error) {
      devError('CrownRipsStore: startCrownRipsGame error', getErrorMessage(error));
      this.setIsLoadingGame(false);
    }
  }

  // Creates the launch session (the slow call) and delivers it to the iframe. Separated so the iframe's
  // session request can re-run it on retry after a failed launch.
  private async launchSession(gameId: number) {
    if (this.launchInFlight) return;
    this.launchInFlight = true;
    try {
      const launchGameResponse = await postRequest<ApiResponse<LaunchGameData>>('user/launch-game', {
        coin: CROWN_RIPS_COIN,
        gameId,
      });
      const gameData = launchGameResponse.data?.data;
      if (!gameData) {
        throw new Error('launchGameResponse not found');
      }

      // Crown Rips open/sell/ship go through the core game-provider, authenticated by the launch
      // sessionId. player-backend returns it under `crownRips.token` (namespaced like metagames/
      // sportsEvents); we build our own game-ui URL. Fall back to the gameUrl query for older builds.
      const sessionId = gameData.crownRips?.token ?? getQueryParam(gameData.gameUrl, 'sessionId');
      if (!sessionId) {
        throw new Error('launch response had no sessionId');
      }

      this.launchSessionId = sessionId;
      this.deliverSession();
    } catch (error) {
      devError('CrownRipsStore: launchSession error', getErrorMessage(error));
      this.iframeWindow?.postMessage({ type: 'crownRipsSessionError' }, import.meta.env.VITE_GAME_UI_URL);
    } finally {
      this.launchInFlight = false;
    }
  }

  // Called by GameUiStore when the iframe posts `crownRipsSessionRequest` (its listener is mounted).
  // Stashes the iframe window, then either delivers the session or (re)starts launch-game — the latter
  // gives the iframe's "Try again" a working retry when launch-game failed.
  public handleSessionRequest(source: Window | null) {
    this.iframeWindow = source;
    if (this.launchSessionId) {
      this.deliverSession();
    } else if (!this.launchInFlight && this.gameId != null) {
      void this.launchSession(this.gameId);
    }
  }

  private deliverSession() {
    if (!this.launchSessionId || !this.iframeWindow) return;
    this.iframeWindow.postMessage(
      { type: 'crownRipsSessionReady', payload: { sessionId: this.launchSessionId } },
      import.meta.env.VITE_GAME_UI_URL,
    );
    this.postSoundEnabled();
  }

  // Mirror the app-wide Sound toggle (UiStore.disabledSounds, the visible profile switch) into the
  // crown-rips iframe — the game has no mute of its own, it follows this. No-op until the iframe window
  // is known; re-sent live by the reaction in the constructor whenever the toggle flips.
  private postSoundEnabled() {
    this.iframeWindow?.postMessage(
      { type: 'setSoundEnabled', payload: { enabled: !this.rootStore.uiStore.disabledSounds } },
      import.meta.env.VITE_GAME_UI_URL,
    );
  }
}
